// @dx hook
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

/**
 * Days since a past date (floor, integer). Negative when the date is in the
 * future, null when absent or unparseable.
 *
 * Exported as a plain function, not just as a `useFormat()` member, because the
 * Excel exporter needs the same number a day-counter cell displays and cannot
 * call a hook from an `exportAs` resolver. One implementation, so the cell and
 * the workbook can't disagree.
 */
export function daysSince(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  const d = typeof value === "string" ? new Date(value) : value;
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}

export const useFormat = () => {
  const { i18n } = useTranslation();

  const formatters = useMemo(() => {
    const locale = i18n.language || "pl";

    return {
      // Standard number formatting
      number: (value: number | null | undefined, decimals: number = 2): string => {
        if (value === null || value === undefined) return "-";
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(value);
      },

      // FX rate formatting — always 4 decimals.
      fxRate: (value: number | null | undefined): string => {
        if (value === null || value === undefined) return "-";
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        }).format(value);
      },

      // Money amount (no currency suffix) — always 2 decimals. Pair with a
      // currency Tag/code when the amount is rendered outside AmountDisplay.
      money: (value: number | null | undefined): string => {
        if (value === null || value === undefined) return "-";
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
      },

      // Weight formatting (always 3 decimals)
      weight: (value: number | null | undefined): string => {
        if (value === null || value === undefined) return "-";
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        }).format(value) + " t";
      },

      // Quantity formatting: pass weight + priceType, the function handles everything.
      // per_ton → "24,500 t", flat_rate → "1 szt (24,500 t)" or "1 szt"
      quantity: (weight: number | null | undefined, priceType: string | null | undefined): string => {
        const fmtW = (v: number) => new Intl.NumberFormat(locale, {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        }).format(v);

        if (priceType !== "per_ton") {
          return "1 szt";
        }
        if (weight === null || weight === undefined) return "-";
        return fmtW(weight) + " t";
      },

      // Currency formatting
      currency: (
        value: number | null , 
        currency: string | null | undefined,
        decimals: number = 2
      ): string => {
        if (value === null || value === undefined || !currency) return "-";
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency: currency,
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(value);
      },

      // Percentage formatting
      percentage: (value: number | null | undefined, decimals: number = 1): string => {
        if (value === null || value === undefined) return "-";
        return new Intl.NumberFormat(locale, {
          style: "percent",
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(value / 100);
      },

      // Compact number formatting (for large numbers)
      compact: (value: number | null | undefined): string => {
        if (value === null || value === undefined) return "-";
        return new Intl.NumberFormat(locale, {
          notation: "compact",
          compactDisplay: "short",
        }).format(value);
      },

      // Integer formatting (no decimals, always group thousands)
      integer: (value: number | null | undefined): string => {
        if (value === null || value === undefined) return "-";
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          useGrouping: "always",
        }).format(value);
      },

      // Date formatting (date only, no time) — always YYYY-MM-DD
      date: (value: string | Date | null | undefined): string => {
        if (!value) return "-";
        const d = typeof value === "string" ? new Date(value) : value;
        if (isNaN(d.getTime())) return "-";
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      },

      // DateTime formatting (date and time) — always YYYY-MM-DD HH:mm
      dateTime: (value: string | Date | null | undefined): string => {
        if (!value) return "-";
        const d = typeof value === "string" ? new Date(value) : value;
        if (isNaN(d.getTime())) return "-";
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const h = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        return `${y}-${mo}-${day} ${h}:${mi}`;
      },

      // Time formatting (time only)
      time: (value: string | Date | null | undefined): string => {
        if (!value) return "-";
        const date = typeof value === "string" ? new Date(value) : value;
        if (isNaN(date.getTime())) return "-";
        return new Intl.DateTimeFormat(locale, {
          hour: "2-digit",
          minute: "2-digit",
        }).format(date);
      },

      // Days since a past date (floor, integer). Negative when date is in the future.
      daysSince,

      // True when the given date falls on today (local calendar day).
      isToday: (value: string | Date | null | undefined): boolean => {
        if (!value) return false;
        const d = typeof value === "string" ? new Date(value) : value;
        if (isNaN(d.getTime())) return false;
        const now = new Date();
        return (
          d.getFullYear() === now.getFullYear() &&
          d.getMonth()    === now.getMonth()    &&
          d.getDate()     === now.getDate()
        );
      },

      // Relative time formatting (e.g., "2 days ago") for timestamps of events
      // that have ALREADY happened — `created_at`, `resolved_at` and friends.
      // Future values are clamped to "now" rather than rendered as future: the
      // timestamp comes from the SERVER clock while the comparison below runs on
      // the BROWSER's, so a workstation a minute behind used to make a
      // just-saved comment read "za 1 minutę". If a caller ever needs a genuine
      // future date (a payment due date, say), this needs an opt-out rather than
      // a change of default — every current caller formats a past event.
      relativeTime: (value: string | Date | null | undefined): string => {
        if (!value) return "-";
        const date = typeof value === "string" ? new Date(value) : value;
        if (isNaN(date.getTime())) return "-";

        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
        const diffInSeconds = Math.min((date.getTime() - Date.now()) / 1000, 0);

        if (Math.abs(diffInSeconds) < 60) {
          return rtf.format(Math.round(diffInSeconds), "second");
        } else if (Math.abs(diffInSeconds) < 3600) {
          return rtf.format(Math.round(diffInSeconds / 60), "minute");
        } else if (Math.abs(diffInSeconds) < 86400) {
          return rtf.format(Math.round(diffInSeconds / 3600), "hour");
        } else if (Math.abs(diffInSeconds) < 2592000) {
          return rtf.format(Math.round(diffInSeconds / 86400), "day");
        } else if (Math.abs(diffInSeconds) < 31536000) {
          return rtf.format(Math.round(diffInSeconds / 2592000), "month");
        } else {
          return rtf.format(Math.round(diffInSeconds / 31536000), "year");
        }
      },
    };
  }, [i18n.language]);

  return formatters;
};